import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Detects and collapses quoted email content in ticket conversations.
 * 
 * Recognizes:
 * - <blockquote> tags (Outlook/Gmail)
 * - Gmail quote divs (class="gmail_quote")
 * - Outlook "On <date>, <person> wrote:" patterns
 * - "-----Original Message-----" dividers
 * - "From: ... Sent: ... To: ... Subject: ..." header blocks
 * - Lines starting with ">" (plain-text quoting)
 */

// Patterns that indicate the START of a quoted section in HTML
const QUOTE_PATTERNS = [
  // Gmail quote div
  /<div\s+class="gmail_quote"[\s\S]*$/i,
  // Outlook appendonsend
  /<div\s+id="appendonsend"[\s\S]*$/i,
  // "On <date>, <person> wrote:" (common pattern)
  /(<br\s*\/?>|\n|<p>)\s*On\s+.{10,80}\s+wrote:\s*(<br\s*\/?>|\n|<\/p>)/i,
  // "-----Original Message-----"
  /(-{3,}\s*Original Message\s*-{3,})/i,
  // "From: ... Sent: ... To: ..." block (Outlook headers)
  /(<br\s*\/?>|\n)\s*From:\s+.{3,}(<br\s*\/?>|\n)\s*Sent:\s+/i,
  // Forwarded message
  /(-{3,}\s*Forwarded message\s*-{3,})/i,
];

// Patterns for plain text (non-HTML) quoting
const PLAIN_QUOTE_PATTERNS = [
  /^>{1,}\s/m,  // Lines starting with >
  /\nOn .{10,80} wrote:\n/,
  /\n-{3,}\s*Original Message\s*-{3,}\n/,
  /\nFrom:\s+.{3,}\nSent:\s+/,
];

function splitQuotedContent(html) {
  if (!html) return { main: '', quoted: '' };
  
  // Check for <blockquote> — most reliable indicator
  const blockquoteIdx = html.indexOf('<blockquote');
  if (blockquoteIdx > 0) {
    return {
      main: html.substring(0, blockquoteIdx),
      quoted: html.substring(blockquoteIdx),
    };
  }
  
  // Check HTML quote patterns
  for (const pattern of QUOTE_PATTERNS) {
    const match = html.match(pattern);
    if (match && match.index > 50) { // Only if there's meaningful content before the quote
      return {
        main: html.substring(0, match.index),
        quoted: html.substring(match.index),
      };
    }
  }
  
  // Check plain-text patterns (for non-HTML content)
  for (const pattern of PLAIN_QUOTE_PATTERNS) {
    const match = html.match(pattern);
    if (match && match.index > 30) {
      return {
        main: html.substring(0, match.index),
        quoted: html.substring(match.index),
      };
    }
  }
  
  // No quoted content detected
  return { main: html, quoted: '' };
}

export default function QuotedReplyCollapse({ html, className = '' }) {
  const [showQuoted, setShowQuoted] = useState(false);
  
  const { main, quoted } = useMemo(() => splitQuotedContent(html), [html]);
  
  if (!quoted) {
    // No quoted content — render normally
    return (
      <div
        className={className}
        style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
        dangerouslySetInnerHTML={{ __html: main }}
      />
    );
  }
  
  return (
    <div className={className} style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
      {/* Main reply content */}
      <div dangerouslySetInnerHTML={{ __html: main }} />
      
      {/* Quoted content toggle */}
      <button
        onClick={(e) => { e.stopPropagation(); setShowQuoted(prev => !prev); }}
        className="inline-flex items-center gap-1 mt-2 mb-1 px-2 py-0.5 text-[11px] font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
        data-testid="quoted-reply-toggle"
        type="button"
      >
        {showQuoted ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {showQuoted ? 'Hide quoted text' : 'Show quoted text'}
      </button>
      
      {/* Collapsed quoted content */}
      {showQuoted && (
        <div
          className="mt-1 pl-3 border-l-2 border-gray-200 text-gray-500 text-xs opacity-75"
          dangerouslySetInnerHTML={{ __html: quoted }}
          data-testid="quoted-reply-content"
        />
      )}
    </div>
  );
}
